import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Sidebar } from '../../components/Sidebar';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import {
  ArrowLeft, Users, IndianRupee, Sparkles, X, MapPin, Trash2,
  Instagram, ExternalLink, ArrowUpRight, Loader2, FileText, GitCompare, Check, ClipboardList,
  Package, Play, GraduationCap, Star, PartyPopper, ChevronLeft, Building2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtNum = (n) => {
  if (!n && n !== 0) return '—';
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toLocaleString();
};
const fmtInr = (n) => (n || n === 0 ? `₹${Number(n).toLocaleString('en-IN')}` : '—');

export const BrandShortlistDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [briefOpen, setBriefOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameForm, setRenameForm] = useState({ name: '', description: '' });

  // Compare mode: when active, rows show a checkbox and a CTA bar appears.
  // Selection is capped at MAX_COMPARE creators (UX: prevents unreadable tables).
  const MAX_COMPARE = 4;
  const [compareMode, setCompareMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const toggleCompareMode = () => {
    setCompareMode((v) => {
      const next = !v;
      if (!next) setSelectedIds([]); // exiting clears the picks
      return next;
    });
  };

  const toggleSelect = (creatorId) => {
    setSelectedIds((prev) => {
      if (prev.includes(creatorId)) return prev.filter((x) => x !== creatorId);
      if (prev.length >= MAX_COMPARE) {
        toast.error(`You can compare up to ${MAX_COMPARE} creators at once`);
        return prev;
      }
      return [...prev, creatorId];
    });
  };

  const launchCompare = () => {
    if (selectedIds.length < 2) {
      toast.error('Pick at least 2 creators to compare');
      return;
    }
    navigate(`/brand/shortlists/${id}/compare?ids=${selectedIds.join(',')}`);
  };

  const load = async () => {
    try {
      const { data: d } = await axios.get(`${API_URL}/api/shortlists/${id}`, { withCredentials: true });
      setData(d);
      setRenameForm({ name: d.name || '', description: d.description || '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load shortlist');
      navigate('/brand/shortlists');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const removeCreator = async (creatorId) => {
    if (!window.confirm('Remove this creator from the shortlist?')) return;
    try {
      await axios.delete(`${API_URL}/api/shortlists/${id}/creators/${creatorId}`, { withCredentials: true });
      toast.success('Removed');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove');
    }
  };

  const rename = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(
        `${API_URL}/api/shortlists/${id}`,
        { name: renameForm.name.trim(), description: renameForm.description.trim() || null },
        { withCredentials: true }
      );
      toast.success('Updated');
      setRenameOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center bg-white dark:bg-[#0A0B0D]">
          <Loader2 className="animate-spin text-[#FF451A]" size={32} />
        </div>
      </div>
    );
  }
  if (!data) return null;

  const stats = data.stats || {};

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D]" data-testid="brand-shortlist-detail-page">
        <div className="max-w-6xl mx-auto">
          <button
            onClick={() => navigate('/brand/shortlists')}
            className="text-sm text-[#6B6B6B] dark:text-zinc-400 hover:text-white flex items-center gap-1 mb-6"
            data-testid="shortlist-back-button"
          >
            <ArrowLeft size={14} /> Back to shortlists
          </button>

          <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
            <div className="min-w-0 flex-1">
              <h1 className="text-4xl font-bold tracking-tight text-[#0F0F0F] dark:text-white mb-1" data-testid="shortlist-title">
                {data.name}
              </h1>
              {data.description && <p className="text-[#6B6B6B] dark:text-zinc-400">{data.description}</p>}
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                onClick={toggleCompareMode}
                disabled={stats.total_creators < 2}
                className={`rounded-full px-4 py-2 text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  compareMode
                    ? 'bg-[#0055FF]/15 border border-[#0055FF]/40 text-[#0055FF] hover:bg-[#0055FF]/20'
                    : 'border border-black/[0.10] dark:border-white/10 bg-white/5 text-[#0F0F0F] dark:text-white hover:bg-white/10'
                } disabled:opacity-40 disabled:cursor-not-allowed`}
                data-testid="toggle-compare-mode-button"
              >
                <GitCompare size={13} /> {compareMode ? 'Exit compare' : 'Compare'}
              </Button>
              <Button
                onClick={() => setRenameOpen(true)}
                className="border border-black/[0.10] dark:border-white/10 bg-white/5 text-[#0F0F0F] dark:text-white hover:bg-white/10 rounded-full px-4 py-2 text-sm"
                data-testid="shortlist-rename-button"
              >
                Edit
              </Button>
              <Button
                onClick={() => navigate(`/brand/plans?from_shortlist=${id}`)}
                disabled={stats.total_creators === 0}
                className="border border-[#FF451A]/40 bg-[#FF451A]/10 text-[#FF451A] hover:bg-[#FF451A]/15 rounded-full px-4 py-2 text-sm font-medium flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="build-plan-button"
              >
                <ClipboardList size={13} /> Build Plan
              </Button>
              <Button
                onClick={() => setBriefOpen(true)}
                disabled={stats.total_creators === 0}
                className="bg-[#FF451A] text-[#0F0F0F] dark:text-white rounded-full px-5 py-2 font-semibold text-sm hover:bg-[#E63E17] shadow-[0_4px_20px_rgba(255,69,26,0.3)] flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="create-brief-from-shortlist-button"
              >
                <Sparkles size={14} /> Create campaign brief
              </Button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <StatTile icon={Users} iconColor="#FF451A" label="Creators" value={stats.total_creators} testid="stat-creator-count" />
            <StatTile icon={IndianRupee} iconColor="#00C972" label="Total Reel cost" value={fmtInr(stats.total_reel_cost)} testid="stat-total-reel" />
            <StatTile icon={IndianRupee} iconColor="#0055FF" label="Total Story cost" value={fmtInr(stats.total_story_cost)} testid="stat-total-story" />
            <StatTile icon={IndianRupee} iconColor="#FFB800" label="Combined est." value={fmtInr(stats.total_estimated_cost)} testid="stat-combined" />
          </div>

          {/* Tag rows */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <TagPanel label="Categories represented" items={stats.categories || []} accent="#FF451A" testid="categories-tags" />
            <TagPanel label="Locations represented" items={stats.cities || []} accent="#0055FF" testid="locations-tags" icon={MapPin} />
          </div>

          {/* Creator rows */}
          {data.creators.length === 0 ? (
            <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-16 text-center" data-testid="shortlist-empty-creators">
              <Users className="mx-auto text-[#6B6B6B] dark:text-zinc-500 mb-3" size={40} />
              <h3 className="text-lg font-semibold text-[#0F0F0F] dark:text-white mb-1">No creators saved yet</h3>
              <p className="text-sm text-[#6B6B6B] dark:text-zinc-400 mb-5">Add creators from the Discovery page.</p>
              <Link
                to="/brand/discover"
                className="inline-flex items-center gap-2 bg-[#FF451A] text-[#0F0F0F] dark:text-white rounded-full px-5 py-2.5 font-medium hover:bg-[#E63E17]"
              >
                Go to Discovery <ArrowUpRight size={14} />
              </Link>
            </div>
          ) : (
            <div className="space-y-2" data-testid="shortlist-creators-list">
              {compareMode && (
                <div className="bg-[#0055FF]/[0.06] border border-[#0055FF]/20 rounded-xl px-4 py-3 text-xs text-zinc-300 flex items-center gap-2" data-testid="compare-mode-hint">
                  <GitCompare size={13} className="text-[#0055FF]" />
                  <span>
                    Pick <span className="text-[#0F0F0F] dark:text-white font-semibold">2–{MAX_COMPARE}</span> creators to compare side-by-side.
                    {selectedIds.length > 0 && (
                      <span className="ml-2 text-[#0055FF] font-mono">
                        {selectedIds.length}/{MAX_COMPARE} selected
                      </span>
                    )}
                  </span>
                </div>
              )}
              {data.creators.map((c) => {
                const igUrl = c.instagram_handle
                  ? `https://instagram.com/${c.instagram_handle.replace(/^@/, '')}`
                  : null;
                const isSelected = selectedIds.includes(c.id);
                return (
                  <div
                    key={c.id}
                    onClick={() => compareMode && toggleSelect(c.id)}
                    className={`bg-white dark:bg-[#0A0B0D] rounded-xl p-4 flex items-center gap-4 flex-wrap transition-all border ${
                      compareMode
                        ? `cursor-pointer ${isSelected ? 'border-[#0055FF] bg-[#0055FF]/[0.06]' : 'border-black/[0.08] dark:border-white/5 hover:border-[#0055FF]/40'}`
                        : 'border-black/[0.08] dark:border-white/5 hover:border-white/10'
                    }`}
                    data-testid="shortlist-creator-row"
                  >
                    {compareMode && (
                      <div
                        className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 transition-colors ${
                          isSelected ? 'bg-[#0055FF] border-[#0055FF] text-[#0F0F0F] dark:text-white' : 'border-black/[0.15] dark:border-white/20 bg-transparent'
                        }`}
                        data-testid={isSelected ? 'compare-creator-checkbox-checked' : 'compare-creator-checkbox'}
                      >
                        {isSelected && <Check size={12} />}
                      </div>
                    )}
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#FF451A] to-[#0055FF] flex items-center justify-center text-[#0F0F0F] dark:text-white text-base font-bold shrink-0">
                      {c.name?.[0]?.toUpperCase() || 'C'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-[#0F0F0F] dark:text-white truncate">{c.name}</div>
                      {c.instagram_handle && (
                        <a
                          href={igUrl}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-xs text-[#6B6B6B] dark:text-zinc-400 hover:text-[#E1306C] inline-flex items-center gap-1 transition-colors"
                        >
                          <Instagram size={11} /> @{c.instagram_handle}
                        </a>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-xs font-mono">
                      <span className="text-[#6B6B6B] dark:text-zinc-400"><span className="text-[#A1A1AA] dark:text-zinc-600">followers</span> <span className="text-[#0F0F0F] dark:text-white font-semibold">{fmtNum(c.instagram_followers)}</span></span>
                      <span className="text-[#6B6B6B] dark:text-zinc-400"><span className="text-[#A1A1AA] dark:text-zinc-600">reel</span> <span className="text-[#0F0F0F] dark:text-white font-semibold">{fmtInr(c.pricing_reel)}</span></span>
                      <span className="text-[#6B6B6B] dark:text-zinc-400"><span className="text-[#A1A1AA] dark:text-zinc-600">story</span> <span className="text-[#0F0F0F] dark:text-white font-semibold">{fmtInr(c.pricing_story)}</span></span>
                    </div>
                    <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                      <Link
                        to={`/brand/creators/${c.id}`}
                        className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-black/[0.10] dark:border-white/10 text-zinc-300 hover:text-white hover:bg-white/10 transition-colors inline-flex items-center gap-1"
                        data-testid="shortlist-view-profile-button"
                      >
                        View profile <ExternalLink size={11} />
                      </Link>
                      <button
                        onClick={() => removeCreator(c.id)}
                        className="text-[#6B6B6B] dark:text-zinc-500 hover:text-[#FF3B30] p-1.5 transition-colors"
                        data-testid="shortlist-remove-creator-button"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Sticky CTA bar that appears once 2+ creators are selected.
            Bottom-fixed so it stays visible while the brand scrolls the list. */}
        {compareMode && selectedIds.length >= 2 && (
          <div
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-30 bg-white dark:bg-[#0A0B0D] border border-[#0055FF]/40 rounded-full shadow-[0_8px_30px_rgba(0,85,255,0.25)] px-5 py-3 flex items-center gap-3"
            data-testid="compare-cta-bar"
          >
            <div className="text-xs text-zinc-300 font-mono">
              <span className="text-[#0055FF] font-bold text-base">{selectedIds.length}</span>
              <span className="text-[#6B6B6B] dark:text-zinc-500"> / {MAX_COMPARE} selected</span>
            </div>
            <Button
              onClick={launchCompare}
              className="bg-[#0055FF] text-[#0F0F0F] dark:text-white rounded-full px-5 py-1.5 text-sm font-semibold hover:bg-[#0044CC] flex items-center gap-1.5"
              data-testid="launch-compare-button"
            >
              <GitCompare size={13} /> Compare creators
            </Button>
            <button
              onClick={() => setSelectedIds([])}
              className="text-xs text-[#6B6B6B] dark:text-zinc-500 hover:text-white px-2"
              data-testid="compare-clear-selection-button"
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {/* Rename dialog */}
      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit shortlist</DialogTitle></DialogHeader>
          <form onSubmit={rename} className="space-y-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider block mb-1 text-[#0F0F0F] dark:text-white">Name</label>
              <Input
                value={renameForm.name}
                onChange={(e) => setRenameForm({ ...renameForm, name: e.target.value })}
                className="rounded-xl bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                data-testid="rename-shortlist-name-input"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider block mb-1 text-[#0F0F0F] dark:text-white">Description</label>
              <Input
                value={renameForm.description}
                onChange={(e) => setRenameForm({ ...renameForm, description: e.target.value })}
                className="rounded-xl bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="submit" className="bg-[#FF451A] text-[#0F0F0F] dark:text-white rounded-full px-5 hover:bg-[#E63E17] flex-1">Save</Button>
              <Button type="button" onClick={() => setRenameOpen(false)} className="border border-black/[0.10] dark:border-white/10 bg-transparent text-[#0F0F0F] dark:text-white hover:bg-white/5 rounded-full px-5">Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Brief creation */}
      <BriefFromShortlistDialog
        open={briefOpen}
        onClose={() => setBriefOpen(false)}
        creators={data.creators}
        defaultName={data.name}
        stats={stats}
        onSent={() => {
          setBriefOpen(false);
          navigate('/brand/campaigns');
        }}
      />
    </div>
  );
};

const StatTile = ({ icon: Icon, iconColor, label, value, testid }) => (
  <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-xl p-4" data-testid={testid}>
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 mb-1.5 font-mono">
      <Icon size={11} style={{ color: iconColor }} /> {label}
    </div>
    <div className="text-2xl font-bold text-[#0F0F0F] dark:text-white">{value === 0 || value ? value : '—'}</div>
  </div>
);

const TagPanel = ({ label, items, accent, testid, icon: Icon }) => (
  <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-5" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 mb-3 font-mono">{label}</div>
    {items.length === 0 ? (
      <div className="text-xs text-[#6B6B6B] dark:text-zinc-500">None yet — add creators to populate.</div>
    ) : (
      <div className="flex flex-wrap gap-1.5">
        {items.map((it) => (
          <span
            key={it}
            className="px-2.5 py-0.5 text-xs rounded-full font-medium flex items-center gap-1"
            style={{ backgroundColor: `${accent}15`, color: accent, border: `1px solid ${accent}30` }}
          >
            {Icon && <Icon size={10} />} {it}
          </span>
        ))}
      </div>
    )}
  </div>
);

// Minimal brief creation — feeds directly into the existing
// POST /api/brand/campaigns/submit endpoint with shortlisted_creator_ids.
// No duplicate creator-selection flow: we pass the shortlist's full id list.
const BriefFromShortlistDialog = ({ open, onClose, creators, defaultName, stats, onSent }) => {
  const { user } = useAuth();
  const isAgency = user?.role === 'agency';
  const [agencyClients, setAgencyClients] = useState([]);
  const [clientBrandId, setClientBrandId] = useState('');
  const [clientsLoading, setClientsLoading] = useState(false);
  // Feature 1 — Brief Templates picker
  const [showTemplates, setShowTemplates] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);

  const [form, setForm] = useState({
    campaign_name: '',
    brief: '',
    budget: '',
    platforms: ['Instagram'],
    follower_range: 'Mid (50K–250K)',
  });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) {
      setForm((f) => ({
        ...f,
        campaign_name: defaultName ? `${defaultName} — Campaign` : '',
        budget: stats?.total_estimated_cost ? String(stats.total_estimated_cost) : '',
      }));
      // Lazy-fetch templates on first open
      if (templates.length === 0 && !templatesLoading) {
        setTemplatesLoading(true);
        axios.get(`${API_URL}/api/brand/brief-templates`, { withCredentials: true })
          .then((r) => setTemplates(r.data || []))
          .catch(() => setTemplates([]))
          .finally(() => setTemplatesLoading(false));
      }
      // Agency users need their client list to pick which brand this brief is for.
      if (isAgency && agencyClients.length === 0 && !clientsLoading) {
        setClientsLoading(true);
        axios.get(`${API_URL}/api/agency/clients`, { withCredentials: true })
          .then((r) => setAgencyClients(r.data?.clients || []))
          .catch(() => setAgencyClients([]))
          .finally(() => setClientsLoading(false));
      }
    } else {
      // Reset picker state on close
      setShowTemplates(true);
      setSelectedTemplateId(null);
    }
  }, [open, defaultName, stats, templates.length, templatesLoading, isAgency, agencyClients.length, clientsLoading]);

  const applyTemplate = (tpl) => {
    const platforms = tpl.platform === 'both'
      ? ['Instagram', 'YouTube']
      : tpl.platform
        ? [tpl.platform]
        : ['Instagram'];
    setForm((prev) => ({
      ...prev,
      brief: tpl.sample_brief_text || prev.brief,
      platforms,
    }));
    setSelectedTemplateId(tpl.template_id);
    setShowTemplates(false);
  };

  const TEMPLATE_ICONS = {
    product_seeding: Package,
    awareness_reel: Play,
    tutorial_how_to: GraduationCap,
    review_campaign: Star,
    festival_seasonal: PartyPopper,
  };

  const togglePlatform = (p) =>
    setForm((f) => ({ ...f, platforms: f.platforms.includes(p) ? f.platforms.filter((x) => x !== p) : [...f.platforms, p] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.campaign_name.trim() || !form.brief.trim()) {
      toast.error('Campaign name and brief are required');
      return;
    }
    if (!creators || creators.length === 0) {
      toast.error('Shortlist has no creators');
      return;
    }
    if (isAgency && !clientBrandId) {
      toast.error('Please select the client brand this brief is for');
      return;
    }
    setSending(true);
    try {
      const payload = {
        campaign_name: form.campaign_name.trim(),
        brief: form.brief.trim(),
        creator_count: creators.length,
        followers_range: form.follower_range,
        platforms: form.platforms.length ? form.platforms : ['Instagram'],
        categories: stats?.categories || [],
        languages: ['Hindi'],
        gender: null,
        locations: stats?.cities || [],
        budget: Number(form.budget) || 0,
        deliverables: ['1 Reel + 2 Stories'],
        quality_grades: ['A', 'B'],
        exclusive_only: false,
        shortlisted_creator_ids: creators.map((c) => c.id),
        template_id: selectedTemplateId,
        client_brand_id: isAgency ? clientBrandId : undefined,
      };
      await axios.post(`${API_URL}/api/brand/campaigns/submit`, payload, { withCredentials: true });
      toast.success('Brief submitted to StrawHat HQ');
      onSent();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {showTemplates ? 'Start from a Template' : 'Create campaign brief'}
          </DialogTitle>
          <DialogDescription className="text-sm text-[#6B6B6B] dark:text-zinc-400">
            {showTemplates
              ? 'Pick a pre-built playbook to pre-fill the brief, or start from scratch.'
              : 'Launch a brief to StrawHat HQ with all creators in this shortlist pre-selected.'}
          </DialogDescription>
        </DialogHeader>

        {showTemplates ? (
          // -------------------------------------------------------------
          // Feature 1 — Template picker view
          // -------------------------------------------------------------
          <div className="mt-4 space-y-3" data-testid="brief-template-picker">
            {templatesLoading && (
              <div className="text-sm text-[#6B6B6B] dark:text-zinc-400 py-6 text-center">
                Loading templates…
              </div>
            )}

            {!templatesLoading && templates.map((tpl) => {
              const Icon = TEMPLATE_ICONS[tpl.template_id] || FileText;
              return (
                <div
                  key={tpl.template_id}
                  className="border border-black/[0.08] dark:border-white/10 rounded-2xl p-4 bg-white dark:bg-[#0F1011] hover:border-[#FF451A]/40 transition-colors"
                  data-testid={`brief-template-${tpl.template_id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-xl bg-[#FF451A]/10 flex items-center justify-center text-[#FF451A] shrink-0">
                      <Icon size={20} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-[#0F0F0F] dark:text-white text-base">{tpl.name}</div>
                      <p className="text-sm text-[#6B6B6B] dark:text-zinc-400 mt-0.5 leading-snug">{tpl.description}</p>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-[11px] font-mono uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500">
                        <span>~{tpl.suggested_creator_count} creators</span>
                        <span>{tpl.suggested_duration_weeks}w duration</span>
                        <span>{tpl.platform === 'both' ? 'Instagram + YouTube' : tpl.platform}</span>
                      </div>
                    </div>
                    <Button
                      onClick={() => applyTemplate(tpl)}
                      className="bg-[#FF451A] text-white hover:bg-[#E63E17] rounded-full px-4 py-2 text-sm font-medium shrink-0"
                      data-testid={`use-template-${tpl.template_id}`}
                    >
                      Use Template
                    </Button>
                  </div>
                </div>
              );
            })}

            {!templatesLoading && (
              <button
                type="button"
                onClick={() => {
                  // Reset applyTemplate-mutated fields so Start-from-scratch is truly blank.
                  setForm((prev) => ({ ...prev, brief: '', platforms: ['Instagram'] }));
                  setSelectedTemplateId(null);
                  setShowTemplates(false);
                }}
                className="w-full mt-2 border border-dashed border-black/[0.15] dark:border-white/15 rounded-2xl p-4 flex items-center justify-center gap-2 text-sm font-medium text-[#6B6B6B] dark:text-zinc-400 hover:border-[#FF451A]/40 hover:text-[#FF451A] transition-colors"
                data-testid="start-from-scratch-btn"
              >
                <FileText size={16} /> Start from scratch
              </button>
            )}
          </div>
        ) : (
        <>
        {selectedTemplateId && (
          <div className="mt-3 mb-1 flex items-center gap-2">
            <button
              type="button"
              onClick={() => { setShowTemplates(true); setSelectedTemplateId(null); }}
              className="inline-flex items-center gap-1 text-xs font-medium text-[#6B6B6B] dark:text-zinc-400 hover:text-[#FF451A] transition-colors"
              data-testid="change-template-btn"
            >
              <ChevronLeft size={14} /> Change template
            </button>
            <span className="text-[11px] font-mono uppercase tracking-wider text-[#FF451A] bg-[#FF451A]/10 rounded-full px-2 py-0.5">
              {templates.find((t) => t.template_id === selectedTemplateId)?.name || selectedTemplateId}
            </span>
          </div>
        )}
        <form onSubmit={submit} className="space-y-3">
          {isAgency && (
            <div data-testid="agency-client-picker">
              <label className="text-xs font-semibold uppercase tracking-wider block mb-1 inline-flex items-center gap-1.5 text-[#0F0F0F] dark:text-white">
                <Building2 size={11} className="text-[#FF451A]" /> Client Brand *
              </label>
              {clientsLoading ? (
                <div className="text-xs text-[#6B6B6B] dark:text-zinc-400 py-2">Loading clients…</div>
              ) : agencyClients.length === 0 ? (
                <div className="text-xs text-[#FFB800] bg-[#FFB800]/10 border border-[#FFB800]/30 rounded-xl p-3" data-testid="agency-no-clients">
                  No client brands assigned. Ask your administrator to link brand accounts to your agency.
                </div>
              ) : (
                <Select value={clientBrandId} onValueChange={setClientBrandId}>
                  <SelectTrigger className="rounded-xl bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="agency-client-select">
                    <SelectValue placeholder="Select the client this brief is for" />
                  </SelectTrigger>
                  <SelectContent>
                    {agencyClients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}{c.industry ? ` · ${c.industry}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          <div className="text-xs text-[#6B6B6B] dark:text-zinc-400 bg-white/5 border border-black/[0.10] dark:border-white/10 rounded-lg p-3 flex items-center gap-2" data-testid="brief-creator-summary">
            <FileText size={13} className="text-[#FF451A] shrink-0" />
            <span><span className="font-semibold text-[#0F0F0F] dark:text-white">{creators?.length || 0}</span> creators from this shortlist will be pre-selected — no duplicate picking.</span>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider block mb-1 text-[#0F0F0F] dark:text-white">Campaign name</label>
            <Input
              value={form.campaign_name}
              onChange={(e) => setForm({ ...form, campaign_name: e.target.value })}
              required
              className="rounded-xl bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
              data-testid="brief-name-input"
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider block mb-1 text-[#0F0F0F] dark:text-white">Brief</label>
            <textarea
              value={form.brief}
              onChange={(e) => setForm({ ...form, brief: e.target.value })}
              required
              rows={4}
              placeholder="What's the campaign about? Key messaging, deliverables, timeline notes…"
              className="w-full rounded-xl text-sm p-3 outline-none focus:border-[#FF451A]/40 bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
              data-testid="brief-text-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider block mb-1 text-[#0F0F0F] dark:text-white">Total budget (₹)</label>
              <Input
                type="number"
                value={form.budget}
                onChange={(e) => setForm({ ...form, budget: e.target.value })}
                placeholder="0"
                className="rounded-xl bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                data-testid="brief-budget-input"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider block mb-1 text-[#0F0F0F] dark:text-white">Platforms</label>
              <div className="flex flex-wrap gap-1">
                {['Instagram', 'YouTube', 'Reels', 'Shorts'].map((p) => (
                  <button
                    type="button"
                    key={p}
                    onClick={() => togglePlatform(p)}
                    className={`px-2 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                      form.platforms.includes(p)
                        ? 'bg-[#FF451A]/15 border-[#FF451A]/40 text-[#FF451A]'
                        : 'bg-white/5 border-black/[0.10] dark:border-white/10 text-[#6B6B6B] dark:text-zinc-400 hover:text-white'
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              type="submit"
              disabled={sending}
              className="bg-[#FF451A] text-[#0F0F0F] dark:text-white rounded-full px-5 hover:bg-[#E63E17] flex-1 disabled:opacity-40"
              data-testid="brief-submit-button"
            >
              {sending ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Sparkles size={14} className="mr-1.5" />}
              Send to StrawHat HQ
            </Button>
            <Button type="button" onClick={onClose} className="border border-black/[0.10] dark:border-white/10 bg-transparent text-[#0F0F0F] dark:text-white hover:bg-white/5 rounded-full px-5">
              <X size={14} className="mr-1" /> Cancel
            </Button>
          </div>
        </form>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
};
